<template>
  <div
    class="side-navigation-menu side-navigation-menu-v2"
    @click="forwardClick"
  >
    <slot />
    <div class="menu-container">
      <dx-tree-view
        expand-event="click"
        width="100%"
        selection-mode="single"
        key-expr="path"
        :select-nodes-recursive="true"
        :select-by-click="true"
        :items="items"
        :ref="treeViewRef"
        @initialized="handleMenuInitialized"
        @item-click="handleItemClick"
        @selection-changed="handleSelectionChange"
        @content-ready="handleSelectionChange"
        @item-context-menu="addMenuItems"
        id="treeview"
      />

      <DxContextMenu
        :ref="'contextMenu'"
        :data-source.sync="menuItems"
        target="#treeview .dx-treeview-item"
        @item-click="contextMenuItemClick"
      />
    </div>
  </div>
</template>

<script>
const treeViewRef = "treeViewRef";
import DxContextMenu from "devextreme-vue/context-menu";

export default {
  props: {
    items: Array,
    selectedItem: String,
    compactMode: Boolean,
  },
  data() {
    return {
      treeViewRef,
      menuItems: [
        { text: "Open link in new tab", id: "new-tab" },
        { text: "Open link in new window", id: "new-window" },
      ],
      selectedTreeItem: undefined,
    };
  },
  computed: {
    contextMenu: function () {
      return this.$refs["contextMenu"].instance;
    },
  },
  methods: {
    addMenuItems(e) {
      this.selectedTreeItem = e.itemData;

      const hasPath = e.itemData.path !== undefined;
      this.contextMenu.option("items[0].visible", hasPath);
      this.contextMenu.option("items[1].visible", hasPath);
    },
    contextMenuItemClick(e) {
      if (e.itemData.id == "new-tab") {
        this.contextMenu.hide(true);
        setTimeout(() => {
          window.open(
            `${this.selectedTreeItem.path.replace(/^\//, "")}`,
            "_blank"
          );
        }, 25);
      } else if (e.itemData.id == "new-window") {
        window.open(
          `${this.selectedTreeItem.path.replace(/^\//, "")}`,
          "",
          "toolbar=0,location=0,scrollbars=1,statusbar=1,menubar=0,resizable=1"
        );
      }
    },
    forwardClick(...args) {
      this.$emit("click", args);
    },

    handleMenuInitialized(event) {
      event.component.option("deferRendering", false);
    },

    handleItemClick(e) {
      if (!e.itemData.path || this.compactMode) {
        return;
      }

      this.$router.push(e.itemData.path);

      const pointerEvent = e.event;
      pointerEvent.stopPropagation();
    },

    handleSelectionChange(e) {
      this.updateSelection();
      const nodeClass = "dx-treeview-node";
      const selectedClass = "dx-state-selected";
      const leafNodeClass = "dx-treeview-node-is-leaf";
      const element = e.element;

      const rootNodes = element.querySelectorAll(
        `.${nodeClass}:not(.${leafNodeClass})`
      );
      Array.from(rootNodes).forEach((node) => {
        node.classList.remove(selectedClass);
      });

      let selectedNode = element.querySelector(
        `.${nodeClass}.${selectedClass}`
      );

      while (selectedNode && selectedNode.parentElement) {
        if (selectedNode.classList.contains(nodeClass)) {
          selectedNode.classList.add(selectedClass);
        }
        selectedNode = selectedNode.parentElement;
      }
    },

    updateSelection() {
      if (!this.treeView) {
        return;
      }

      this.treeView.selectItem(this.$route.path);
    },
  },
  mounted() {
    this.treeView = this.$refs[treeViewRef] && this.$refs[treeViewRef].instance;
    this.updateSelection();
    if (this.compactMode) {
      this.treeView.collapseAll();
    }
  },
  watch: {
    $route() {
      this.updateSelection();
    },
    compactMode() {
      if (this.compactMode) {
        this.treeView.collapseAll();
      }
    },
  },
  components: { DxContextMenu },
};
</script>

<style lang="scss">
@import "@/assets/styles/gc-variables.scss";
@import "@/assets/styles/variables.additional.scss";
.side-navigation-menu {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  height: 100%;
  width: 200px !important;

  .menu-container {
    min-height: 100%;
    display: flex;
    flex: 1;

    .dx-treeview {
      // ## Long text positioning
      white-space: nowrap;
      // ##

      // ## Icon width customization
      .dx-treeview-item {
        padding-left: 0;
        padding-right: 0;

        .dx-icon {
          width: $side-panel-min-width !important;
          margin: 0 !important;
        }
      }
      // ##

      // ## Arrow customization
      .dx-treeview-node {
        padding: 0 0 !important;
      }

      .dx-treeview-toggle-item-visibility {
        right: 10px;
        left: auto;
      }

      .dx-rtl .dx-treeview-toggle-item-visibility {
        left: 10px;
        right: auto;
      }
      // ##

      // ## Item levels customization
      .dx-treeview-node {
        &[aria-level="1"] {
          font-weight: bold;
          border-bottom: 1px solid $base-border-color;
        }

        &[aria-level="2"] .dx-treeview-item-content {
          font-weight: normal;
          padding: 0 $side-panel-min-width;
        }
      }
      // ##
    }

    // ## Selected & Focuced items customization
    .dx-treeview {
      .dx-treeview-node-container {
        .dx-treeview-node {
          &.dx-state-selected:not(.dx-state-focused) > .dx-treeview-item {
            background: transparent;
          }

          &.dx-state-selected > .dx-treeview-item * {
            color: $base-accent;
          }

          &:not(.dx-state-focused) > .dx-treeview-item.dx-state-hover {
            background-color: lighten($base-bg, 4);
          }
        }
      }
    }

    .dx-theme-generic .dx-treeview {
      .dx-treeview-node-container
        .dx-treeview-node.dx-state-selected.dx-state-focused
        > .dx-treeview-item
        * {
        color: inherit;
      }
    }
    // ##
    .dx-treeview-item {
      .dx-item-content.dx-treeview-item-content > span {
        padding-left: 10px;
      }
      .dx-item-content.dx-treeview-item-content > i + span {
        padding-left: 0px;
      }
    }
  }
}

.menu-icon {
  width: 1em;
  height: 1em;
  -webkit-filter: grayscale(100%) brightness(5);
  filter: grayscale(100%) brightness(5);
}

.side-navigation-menu-v2 {
  .icon-20 {
    font-size: 15px !important;
  }
}
</style>
